/**
 * Checkbox and Switch — the two boolean controls.
 *
 * Checkbox is used by: the role permission matrix (hundreds of rows — it must
 * stay cheap), product flags (chay / có cồn / cho phép giảm giá), table
 * multi-select in list screens.
 *
 * Switch is used by: settings rows and anything that takes effect immediately
 * ("Kích hoạt chi nhánh", "Bật ngôn ngữ"). The rule the codebase follows:
 * a Checkbox is part of a form you submit, a Switch applies on toggle.
 *
 * Accessibility: both wrap a REAL `<input>` so keyboard, form submission and
 * screen readers work natively; the visual is painted with peer-* classes on a
 * sibling. Hit area is the whole label row, and at least 44px tall.
 */
import { forwardRef } from 'react';
import type { InputHTMLAttributes, ReactNode } from 'react';
import { cn } from '../utils.js';

export interface CheckboxProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type' | 'size'> {
  readonly label?: ReactNode;
  readonly description?: ReactNode;
  /** Renders the "some but not all children selected" dash. */
  readonly indeterminate?: boolean;
  /** Compact mode for matrix grids where the label lives in the row header. */
  readonly bare?: boolean;
}

export const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(function Checkbox(
  { label, description, indeterminate = false, bare = false, className, disabled, ...rest },
  ref,
) {
  const box = (
    <span className="relative inline-flex shrink-0 items-center justify-center">
      <input
        ref={(node) => {
          if (node !== null) node.indeterminate = indeterminate;
          if (typeof ref === 'function') ref(node);
          else if (ref !== null) ref.current = node;
        }}
        type="checkbox"
        disabled={disabled}
        className={cn(
          'peer h-5 w-5 shrink-0 cursor-pointer appearance-none rounded-sm',
          'bg-surface ring-1 ring-inset ring-line-strong',
          'checked:bg-brand-600 checked:ring-brand-600',
          'indeterminate:bg-brand-600 indeterminate:ring-brand-600',
          'outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-canvas',
          'disabled:cursor-not-allowed disabled:opacity-50',
          bare ? className : undefined,
        )}
        {...rest}
      />
      <svg
        viewBox="0 0 16 16"
        aria-hidden="true"
        className="pointer-events-none absolute h-3.5 w-3.5 text-white opacity-0 peer-checked:opacity-100 peer-indeterminate:opacity-0"
      >
        <path d="M3 8.5 6.2 11.7 13 5" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" />
      </svg>
      <svg
        viewBox="0 0 16 16"
        aria-hidden="true"
        className="pointer-events-none absolute h-3.5 w-3.5 text-white opacity-0 peer-indeterminate:opacity-100"
      >
        <path d="M3.5 8h9" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" />
      </svg>
    </span>
  );

  if (bare) return box;

  return (
    <label
      className={cn(
        'flex min-h-touch cursor-pointer select-none items-start gap-3 py-2',
        disabled === true && 'cursor-not-allowed opacity-60',
        className,
      )}
    >
      <span className="pt-0.5">{box}</span>
      {label !== undefined || description !== undefined ? (
        <span className="flex flex-col">
          {label !== undefined ? <span className="text-sm font-medium text-fg">{label}</span> : null}
          {description !== undefined ? <span className="text-xs text-fg-subtle">{description}</span> : null}
        </span>
      ) : null}
    </label>
  );
});

export interface SwitchProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type' | 'size'> {
  readonly label?: ReactNode;
  readonly description?: ReactNode;
}

export const Switch = forwardRef<HTMLInputElement, SwitchProps>(function Switch(
  { label, description, className, disabled, ...rest },
  ref,
) {
  return (
    <label
      className={cn(
        'flex min-h-touch cursor-pointer select-none items-center justify-between gap-4 py-2',
        disabled === true && 'cursor-not-allowed opacity-60',
        className,
      )}
    >
      {label !== undefined || description !== undefined ? (
        <span className="flex flex-col">
          {label !== undefined ? <span className="text-sm font-medium text-fg">{label}</span> : null}
          {description !== undefined ? <span className="text-xs text-fg-subtle">{description}</span> : null}
        </span>
      ) : null}
      <span className="relative inline-flex shrink-0">
        {/* role="switch" is set on the real input so assistive tech announces
            "bật/tắt" rather than "checkbox". */}
        <input
          ref={ref}
          type="checkbox"
          role="switch"
          disabled={disabled}
          className={cn(
            'peer h-6 w-11 cursor-pointer appearance-none rounded-full bg-line-strong transition-colors',
            'checked:bg-brand-600',
            'outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-canvas',
            'disabled:cursor-not-allowed',
          )}
          {...rest}
        />
        <span
          aria-hidden="true"
          className="pointer-events-none absolute left-0.5 top-0.5 h-5 w-5 rounded-full bg-white shadow transition-transform peer-checked:translate-x-5"
        />
      </span>
    </label>
  );
});
